import type { RouteRecordRaw } from 'vue-router';

/**
 * Textile Dyeing industry pack screens (M2 WP2.6 remainder B, ADR-041): roll grades, shade lots and
 * rolls (register, greige receipt, split/merge/remeasure/regrade/transfer, forward/backward
 * genealogy trace, HU-sum-vs-ledger reconciliation). Kept in its own file, gated by the
 * `textile-dyeing` module entitlement, per the parallel-work convention (mirrors
 * `routes/inventory.ts`) — this file is never touched by the generic Inventory work packages.
 *
 * Every generic inventory field (item, location, lot, ownership) is read/written through the
 * generic `/inventory/*` endpoints elsewhere in the app; these pages only ever call `/textile/*`
 * (AGENTS.md rule 1 — dyeing-specific behavior stays out of the generic Inventory screens).
 */
export const textileRoutes: RouteRecordRaw[] = [
  {
    path: 'textile/roll-grades',
    name: 'textile-roll-grades',
    component: () => import('../pages/textile/RollGradesPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/shade-lots',
    name: 'textile-shade-lots',
    component: () => import('../pages/textile/ShadeLotsPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/shade-lots/:id',
    name: 'textile-shade-lot-detail',
    component: () => import('../pages/textile/ShadeLotDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/rolls',
    name: 'textile-rolls',
    component: () => import('../pages/textile/RollsPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/rolls/reconciliation',
    name: 'textile-rolls-reconciliation',
    component: () => import('../pages/textile/RollReconciliationPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/rolls/:huId',
    name: 'textile-roll-detail',
    component: () => import('../pages/textile/RollDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },

  // ---- Dye batches / recipes / route templates / re-dye / traceability (M5 WP5.4, DECISIONS.md
  // "ADR-064 — Batch card & textile dyeing screens (WP5.4)"). The print card itself
  // (`textile/dye-batches/:id/card`) is deliberately NOT here — it is a top-level, chrome-free
  // route registered directly in `router.ts` (print CSS/@media print needs no app shell/sidebar).
  {
    path: 'textile/dye-batches',
    name: 'textile-dye-batches',
    component: () => import('../pages/textile/dyeing/DyeBatchesPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/dye-batches/:id',
    name: 'textile-dye-batch-detail',
    component: () => import('../pages/textile/dyeing/DyeBatchDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/dye-recipes/:specVersionId',
    name: 'textile-dye-recipe-editor',
    component: () => import('../pages/textile/dyeing/DyeRecipeEditorPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/route-templates',
    name: 'textile-route-templates',
    component: () => import('../pages/textile/dyeing/RouteTemplatesPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/redye',
    name: 'textile-redye',
    component: () => import('../pages/textile/dyeing/RedyeRequestPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/redye/:orderId',
    name: 'textile-redye-detail',
    component: () => import('../pages/textile/dyeing/ReworkDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/traceability',
    name: 'textile-traceability',
    component: () => import('../pages/textile/dyeing/TraceabilityExplorerPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },

  // ---- Job work orders (M8 WP8.2w, DECISIONS.md "ADR-077 — Sales & Job Work (M8)" backend,
  // "ADR-078 — Sales & Job Work web screens" this pass): commission dyeing of customer-owned greige
  // against an existing dye batch. Gated by the `textile-dyeing` module entitlement, same as every
  // other screen in this file — the server additionally gates every write behind the
  // `textile.jobWork` feature flag (`feature-not-entitled`, `job-work.service.ts`), which the web
  // client surfaces as an ordinary problem+json toast (no client-side feature list exists yet, same
  // as the rest of this app — session only carries modules + permissions).
  {
    path: 'textile/job-work-orders',
    name: 'textile-job-work-orders',
    component: () => import('../pages/textile/job-work/JobWorkOrdersPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/job-work-orders/new',
    name: 'textile-job-work-order-new',
    component: () => import('../pages/textile/job-work/CreateJobWorkOrderPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/job-work-orders/:id',
    name: 'textile-job-work-order-detail',
    component: () => import('../pages/textile/job-work/JobWorkOrderDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },

  // ---- Color Lab / recipe development (M8 WP8.5, DECISIONS.md "ADR-088").
  {
    path: 'textile/color-lab',
    name: 'textile-color-lab-requests',
    component: () => import('../pages/textile/color-lab/ShadeRequestsPage.vue'),
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/color-lab/requests/:id',
    name: 'textile-color-lab-request',
    component: () => import('../pages/textile/color-lab/ShadeRequestDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
  {
    path: 'textile/color-lab/lab-dips/:id',
    name: 'textile-color-lab-dip',
    component: () => import('../pages/textile/color-lab/LabDipDetailPage.vue'),
    props: true,
    meta: { requiresModule: 'textile-dyeing' },
  },
];
